import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Text,
  Stack,
  Group,
  Badge,
  Alert,
  LoadingOverlay,
  Box,
  Divider,
  Title
} from '@mantine/core';
import { IconArrowLeft, IconEye, IconPlayerPlay, IconCheck, IconAlertCircle } from '@tabler/icons-react';

import { ActionButton } from '../common/ActionButton';
import { showSuccessNotification } from '../../utils/notifications';
import { getStatusColor, getStatusLabel, DisplayStatus } from '../../types/status';

// Mock report data - in real implementation this would come from API
const mockReportData = {
  'q4-2024-tu---safe-t': {
    quarter: 'Q4 2024',
    projectName: 'SAFE-T',
    subawardee: 'TU - SAFE-T',
    dueDate: '2024-01-15',
    submittedDate: null,
    status: 'not_submitted' as DisplayStatus,
    reportContent: {
      generalInfo: {
        organizationName: 'TU - SAFE-T',
        reportingPeriod: 'Q4 2024',
        preparedBy: 'Not Submitted',
        preparedDate: null
      },
      performance: {
        milestonesCompleted: 0,
        milestonesTotal: 12,
        budgetUtilized: 0,
        keyAccomplishments: [],
        challenges: ['Report not yet submitted']
      },
      financials: {
        totalBudget: 180000,
        amountSpent: 0,
        remainingBudget: 180000,
        expenditureBreakdown: {
          personnel: 0,
          equipment: 0,
          travel: 0,
          other: 0
        }
      }
    }
  },
  'q1-2025-asemio': {
    quarter: 'Q1 2025',
    projectName: 'ASWP',
    subawardee: 'Asemio',
    dueDate: '2024-04-15',
    submittedDate: '2024-04-20',
    status: 'pending' as DisplayStatus,
    reportContent: {
      generalInfo: {
        organizationName: 'Asemio',
        reportingPeriod: 'Q1 2025',
        preparedBy: 'Sarah Johnson',
        preparedDate: '2024-04-20'
      },
      performance: {
        milestonesCompleted: 7,
        milestonesTotal: 8,
        budgetUtilized: 92,
        keyAccomplishments: [
          'Deployed advanced sensor network',
          'Completed stakeholder engagement sessions',
          'Exceeded performance benchmarks by 15%'
        ],
        challenges: [
          'Minor delays in vendor deliveries',
          'Weather impacted field testing'
        ]
      },
      financials: {
        totalBudget: 320000,
        amountSpent: 294400,
        remainingBudget: 25600,
        expenditureBreakdown: {
          personnel: 200000,
          equipment: 75000,
          travel: 15400,
          other: 4000
        }
      }
    }
  },
  'q1-2025-atlas': {
    quarter: 'Q1 2025',
    projectName: 'ASWP',
    subawardee: 'Atlas',
    dueDate: '2024-04-15',
    submittedDate: '2024-04-22',
    status: 'pending' as DisplayStatus,
    reportContent: {
      generalInfo: {
        organizationName: 'Atlas',
        reportingPeriod: 'Q1 2025',
        preparedBy: 'Michael Chen',
        preparedDate: '2024-04-22'
      },
      performance: {
        milestonesCompleted: 6,
        milestonesTotal: 9,
        budgetUtilized: 78,
        keyAccomplishments: [
          'Infrastructure upgrades completed',
          'Training program launched',
          'Community partnerships established'
        ],
        challenges: [
          'Supply chain interruptions',
          'Permitting delays for construction'
        ]
      },
      financials: {
        totalBudget: 275000,
        amountSpent: 214500,
        remainingBudget: 60500,
        expenditureBreakdown: {
          personnel: 150000,
          equipment: 45000,
          travel: 12500,
          other: 7000
        }
      }
    }
  },
  'q1-2025-thec': {
    quarter: 'Q1 2025',
    projectName: 'ASWP',
    subawardee: 'THEC',
    dueDate: '2024-04-15',
    submittedDate: '2024-04-15',
    status: 'in_review' as DisplayStatus,
    reportContent: {
      generalInfo: {
        organizationName: 'THEC',
        reportingPeriod: 'Q1 2025',
        preparedBy: 'David Wilson',
        preparedDate: '2024-04-15'
      },
      performance: {
        milestonesCompleted: 9,
        milestonesTotal: 10,
        budgetUtilized: 88,
        keyAccomplishments: [
          'Regulatory compliance achieved',
          'Safety protocols implemented',
          'Staff certification completed'
        ],
        challenges: [
          'Coordination with multiple agencies'
        ]
      },
      financials: {
        totalBudget: 195000,
        amountSpent: 171600,
        remainingBudget: 23400,
        expenditureBreakdown: {
          personnel: 120000,
          equipment: 35000,
          travel: 10600,
          other: 6000
        }
      }
    }
  },
  'q3-2025-black-tech-street': {
    quarter: 'Q3 2025',
    projectName: 'G-ACE',
    subawardee: 'Black Tech Street',
    dueDate: '2025-10-15',
    submittedDate: '2025-10-10',
    status: 'in_review' as DisplayStatus,
    reportContent: {
      generalInfo: {
        organizationName: 'Black Tech Street',
        reportingPeriod: 'Q3 2025',
        preparedBy: 'Angela Rodriguez',
        preparedDate: '2025-10-10'
      },
      performance: {
        milestonesCompleted: 11,
        milestonesTotal: 12,
        budgetUtilized: 94,
        keyAccomplishments: [
          'Technology platform launched',
          'User engagement exceeded targets',
          'Partnership agreements finalized',
          'Beta testing successfully completed'
        ],
        challenges: [
          'Minor user interface adjustments needed'
        ]
      },
      financials: {
        totalBudget: 450000,
        amountSpent: 423000,
        remainingBudget: 27000,
        expenditureBreakdown: {
          personnel: 280000,
          equipment: 85000,
          travel: 18000,
          other: 40000
        }
      }
    }
  }
};

export function InitiativeLeadReportView() {
  const { reportId, subawardee } = useParams();
  const navigate = useNavigate();
  const [isStartingReview, setIsStartingReview] = useState(false);

  // In real implementation, this would be an API call
  const reportKey = `${reportId}-${subawardee?.toLowerCase().replace(/\s+/g, '-')}`;
  const reportData = mockReportData[reportKey as keyof typeof mockReportData];

  const handleBack = () => {
    navigate('/');
  };

  const handleStartReview = () => {
    setIsStartingReview(true);
    // In real implementation, this would update the report status to 'in_review' via API
    setTimeout(() => {
      setIsStartingReview(false);
      // Show success message and potentially navigate to a review interface
      showSuccessNotification('Review process started. The report status has been updated to "In Review".');
    }, 1000);
  };

  const handleViewFullReport = () => {
    // Navigate to full report view for detailed editing/review
    navigate(`/reports/${reportId}`);
  };

  if (!reportData) {
    return (
      <Container size="md" py="xl">
        <Alert 
          icon={<IconAlertCircle size={16} />} 
          title="Report Not Found" 
          color="red"
        >
          The requested report could not be found. Please check the report ID and try again.
        </Alert>
      </Container>
    );
  }

      return (
      <>
        <LoadingOverlay 
          visible={isStartingReview} 
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 9999
          }}
        />
        <Container size="md" py="xl">
          <Stack spacing="xl">
        {/* Header */}
        <Group position="apart" align="flex-start">
          <Box>
                         <Group spacing="sm" mb="xs">
               <ActionButton
                 onClick={handleBack}
                 label="Back to Dashboard"
                 ariaLabel="Return to the admin dashboard"
                 actionType="secondary"
                 leftIcon={<IconArrowLeft size={16} />}
                 sx={{ 
                   height: 32, 
                   fontSize: '14px',
                   border: 'none',
                   '&:hover': {
                     border: 'none'
                   }
                 }}
               />
             </Group>
            <Title order={2}>
              {reportData.quarter} Report - {reportData.subawardee}
            </Title>
            <Text color="dimmed" size="sm">
              {reportData.projectName} • Due: {new Date(reportData.dueDate).toLocaleDateString()}
            </Text>
          </Box>
          <Badge 
            color={getStatusColor(reportData.status)} 
            variant="light" 
            size="lg"
          >
            {getStatusLabel(reportData.status)}
          </Badge>
        </Group>

        {/* Report Status Info */}
        <Paper p="md" withBorder>
          <Stack spacing="sm">
            <Text weight={600} size="sm">Report Information</Text>
            <Group spacing="xl">
              <Box>
                <Text size="xs" color="dimmed">Submitted Date</Text>
                <Text size="sm" weight={500}>
                  {reportData.submittedDate ? new Date(reportData.submittedDate).toLocaleDateString() : 'Not submitted'}
                </Text>
              </Box>
              <Box>
                <Text size="xs" color="dimmed">Current Status</Text>
                <Text size="sm" weight={500}>{getStatusLabel(reportData.status)}</Text>
              </Box>
              <Box>
                <Text size="xs" color="dimmed">Days Since Submission</Text>
                <Text size="sm" weight={500}>
                  {reportData.submittedDate 
                    ? Math.ceil((Date.now() - new Date(reportData.submittedDate).getTime()) / (1000 * 60 * 60 * 24))
                    : 'N/A'
                  }
                </Text>
              </Box>
            </Group>
          </Stack>
        </Paper>

        {/* Report Summary */}
        <Paper p="lg" withBorder>
          <Stack spacing="lg">
            <Group position="apart">
              <Text weight={600} size="lg">Report Summary</Text>
              <ActionButton
                onClick={handleViewFullReport}
                label="View Full Report"
                ariaLabel="Open the full detailed report"
                actionType="secondary"
                leftIcon={<IconEye size={16} />}
                sx={{ height: 32, fontSize: '14px' }}
              />
            </Group>

            <Divider />

            {/* Performance Overview */}
            <Box>
              <Text weight={600} mb="sm">Performance Overview</Text>
              <Group spacing="xl">
                <Box>
                  <Text size="xs" color="dimmed">Milestones Completed</Text>
                  <Text size="lg" weight={700}>
                    {reportData.reportContent.performance.milestonesCompleted} / {reportData.reportContent.performance.milestonesTotal}
                  </Text>
                </Box>
                <Box>
                  <Text size="xs" color="dimmed">Budget Utilization</Text>
                  <Text size="lg" weight={700}>
                    {reportData.reportContent.performance.budgetUtilized}%
                  </Text>
                </Box>
              </Group>
            </Box>

            <Divider />

            {/* Key Accomplishments */}
            <Box>
              <Text weight={600} mb="sm">Key Accomplishments</Text>
              <Stack spacing="xs">
                {reportData.reportContent.performance.keyAccomplishments.map((accomplishment, index) => (
                  <Group key={index} spacing="xs">
                    <IconCheck size={16} color="green" />
                    <Text size="sm">{accomplishment}</Text>
                  </Group>
                ))}
              </Stack>
            </Box>

            {/* Challenges */}
            {reportData.reportContent.performance.challenges.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Text weight={600} mb="sm">Challenges & Issues</Text>
                  <Stack spacing="xs">
                    {reportData.reportContent.performance.challenges.map((challenge, index) => (
                      <Group key={index} spacing="xs">
                        <IconAlertCircle size={16} color="orange" />
                        <Text size="sm">{challenge}</Text>
                      </Group>
                    ))}
                  </Stack>
                </Box>
              </>
            )}
          </Stack>
        </Paper>

        {/* Action Buttons */}
        <Paper p="lg" withBorder>
          <Stack spacing="md">
            <Text weight={600}>Review Actions</Text>
            <Text size="sm" color="dimmed">
              Review the report summary above. When ready, you can start the formal review process to provide feedback and approve or request changes.
            </Text>
            <Group>
              <ActionButton
                onClick={handleStartReview}
                label={reportData.status === 'in_review' ? 'Review In Progress' : 
                       reportData.status === 'approved' ? 'Already Approved' : 
                       'Start Review Process'}
                ariaLabel="Start the formal review process for this report"
                actionType="primary"
                leftIcon={<IconPlayerPlay size={16} />}
                disabled={reportData.status === 'in_review' || reportData.status === 'approved'}
                isLoading={isStartingReview}
              />
              <ActionButton
                onClick={handleViewFullReport}
                label="View Full Report Details"
                ariaLabel="Open the detailed report view for comprehensive review"
                actionType="secondary"
                leftIcon={<IconEye size={16} />}
              />
            </Group>
          </Stack>
        </Paper>
      </Stack>
    </Container>
    </>
  );
} 